/* eslint-disable no-param-reassign */
import { DomSanitizer } from '@angular/platform-browser';
import {
  Component,
  EventEmitter,
  Output,
  Input,
  ViewChild,
  ChangeDetectionStrategy,
  OnDestroy,
  AfterViewInit,
  ChangeDetectorRef,
  ViewEncapsulation,
  QueryList,
  ElementRef,
} from '@angular/core';
import { IonInfiniteScroll, ModalController } from '@ionic/angular';
import { Store, select } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { Subject, Observable } from 'rxjs';
import { Capacitor } from '@capacitor/core';
// import 'hammerjs';
import { trigger, style, animate, transition } from '@angular/animations';

import { PlatformService } from 'app/core/services/platform.service';
import * as rootReducer from 'app/core/store';
import * as fromSelectors from 'app/core/store/selectors';
import * as fromOfflineSelector from 'app/core/store/selectors/offline.selectors';
import { Delivery, InboxDelivery } from 'app/core/models';
import * as fromDeliveryAction from 'app/core/store/actions/delivery.actions';
import { updateDeliveryFromServer } from 'app/core/store/actions/offline.actions';
import { setScrollPosition } from 'app/inbox/inbox-deliveries/store/inbox.actions';
import { ConnectionService } from 'app/core/services/connection.service';
import { DeliveryService } from 'app/core/services/delivery.service';
import { ModalOfflineDeliveryComponent } from '../../modals/modal-offline-delivery/modal-offline-delivery.component';
import { DeliveryListService } from '../delivery-list.service';
import { ModalMetadataComponent } from '../../modals/modal-metadata/modal-metadata.component';
import { ModalsService } from '../../modals/modals.service';

@Component({
  standalone: false,
  selector: 'pptl-delivery-list-view',
  templateUrl: 'delivery-list-view.component.html',
  styleUrls: ['delivery-list-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fade', [
      transition('void => *', [style({ opacity: 0 }), animate(2000, style({ opacity: 1 }))]),
    ]),
  ],
})
export class DeliveryListViewComponent implements OnDestroy, AfterViewInit {
  @Input() deliveries: InboxDelivery[] | Delivery[];
  @Input() parentId?: 'current' | 'favorite' | 'history' | 'offline';
  @Input() blockRedirect = false;
  @Input() scrollPosition = 0;
  @Input() isLoading = false;
  @Output() chooseDelivery: EventEmitter<any> = new EventEmitter<any>();
  @Output() scrollList: EventEmitter<any> = new EventEmitter<any>();
  @Output() pullDown: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(IonInfiniteScroll)
  infiniteScroll: IonInfiniteScroll;

  @ViewChild('deliveryList') deliveryContainer;
  @ViewChild('deliveryListRows') deliveryListRows: QueryList<ElementRef>;

  noTrack = '/assets/images/no-track.jpg';
  isDeliveryShowed = true;
  isHistoryPage = false;
  isDownloadDisabled$: Observable<boolean>;
  downloadConf$: Observable<any>;
  refreshing = false;
  isScrolled = false;

  private destroy$: Subject<boolean> = new Subject();
  private isOfflineModalShown = false;
  private isInfinityScrollShown = true;

  constructor(
    private platformService: PlatformService,
    private store: Store<rootReducer.State>,
    public modalController: ModalController,
    private deliveryListService: DeliveryListService,
    private sanitize: DomSanitizer,
    private deliveryService: DeliveryService,
    private connectionService: ConnectionService,
    private cd: ChangeDetectorRef,
    private modalsService: ModalsService,
  ) {
    this.setHistoryPage();
    this.setDownloadConf();
    this.isDownloadDisabled$ = this.store.pipe(select(fromOfflineSelector.selectCacheDisabled));
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.deliveryContainer && this.deliveries && this.deliveries.length > 10) {
        this.deliveryContainer.scrollToPoint(0, this.scrollPosition);
      }
    }, 350);
  }

  refresh(event, delivery): void {
    event.stopPropagation();
    this.store.dispatch(updateDeliveryFromServer({ deliveryKeyCode: delivery.deliveryKeyCode }));
  }

  get isDevicePlatform(): boolean {
    return this.platformService.isCordova() && !this.platformService.isDesktop();
  }

  get isCurrentPage(): boolean {
    return this.parentId === 'current';
  }

  trackByDeliveryKeyCode(index, delivery) {
    return delivery && delivery.deliveryKeyCode;
  }

  imgLoadedErr(delivery) {
    delivery.deliveryImage = this.noTrack;
    delivery.mobileDeliveryImage = this.noTrack;
  }

  goToDelivery(ev, delivery: Delivery) {
    ev?.stopPropagation();
    this.setScrollPosition();
    if (this.parentId === 'offline') {
      if (this.deliveryService.isOfflineDeliveryExpired(delivery)) {
        this.showOfflineModal(delivery);
      } else {
        this.chooseDelivery.emit(delivery);
      }
    } else if (!delivery.expired) {
      this.chooseDelivery.emit(delivery);
    }
  }

  getRowClass(delivery: InboxDelivery) {
    const downloaded = delivery.offlineStatus === 'downloaded';
    const downloading = delivery.offlineStatus === 'downloading';

    return {
      downloading,
      downloaded,
      visited: delivery.openedDelivery,
    };
  }

  convertDate(date) {
    return this.deliveryListService.convertDate(date);
  }

  async holdHandler(delivery: Delivery) {
    if (this.isDevicePlatform && !delivery.expired) {
      this.store.dispatch(fromDeliveryAction.LoadDelivery({ payload: delivery.deliveryKeyCode }));
      this.modalsService.showModal({
        component: ModalMetadataComponent,
        cssClass: 'modal-metadata',
      });
    }
  }

  onScroll(event: any): void {
    this.scrollList.emit(event);
  }

  ionRefresh(event: any): void {
    this.hideInfinityScroll();
    this.pullDown.emit(event);
    this.refreshing = true;
    this.completeRefresh(event);
  }

  getDeliveryClasses() {
    return {
      mobile: this.isMobile && !this.isDesktop && !this.isTablet,
      desktop: this.isDesktop || (this.isMobileWeb && this.isTablet),
    };
  }

  get isMobile(): boolean {
    return this.platformService.isMobile();
  }

  get isTablet(): boolean {
    return this.platformService.isTablet();
  }

  get isMobileWeb() {
    return this.platformService.isMobileWeb();
  }

  get isDesktop(): boolean {
    return this.platformService.isDesktop();
  }

  get isOffline(): boolean {
    return !this.connectionService.isOnline;
  }

  getDeliveryImage(delivery: Delivery): any {
    let imageUrl;

    if (delivery.mobileDeliveryImage) {
      imageUrl = delivery.mobileDeliveryImage;
    } else {
      imageUrl = delivery.deliveryImage || this.noTrack;
    }

    if (this.isCachedUrl(imageUrl)) {
      const convertUrl = Capacitor.convertFileSrc(imageUrl).replace('undefined/', '');
      if (this.platformService.isIos()) {
        imageUrl = this.sanitize.bypassSecurityTrustResourceUrl(convertUrl);
      } else {
        imageUrl = convertUrl;
      }
    }

    return imageUrl;
  }

  showInfinityScroll(): boolean {
    return this.parentId !== 'offline' && this.isInfinityScrollShown;
  }

  private completeRefresh(event) {
    event.target.disabled = true;
    event.target.complete();

    setTimeout(() => {
      this.refreshing = false;
      this.cd.detectChanges();
      event.target.disabled = false;
    }, 1000);
  }

  private isCachedUrl(url): boolean {
    return this.isMobile && url && url.includes('file://');
  }

  private setHistoryPage(): void {
    this.store.pipe(select(fromSelectors.getUrl), take(1)).subscribe((resp: string) => {
      this.isHistoryPage = resp.includes('/history');
    });
  }

  private setDownloadConf(): void {
   this.downloadConf$ = this.store.pipe(select(fromOfflineSelector.selectDownloadConfig));
  }

  private async showOfflineModal(delivery: Delivery) {
    if (!this.isOfflineModalShown) {
      this.isOfflineModalShown = true;

      const onModalClose = await this.modalsService.showModal({
        component: ModalOfflineDeliveryComponent,
        cssClass: 'modal-offline-delivery',
        backdropDismiss: false,
        componentProps: {
          delivery,
        },
      });

      onModalClose.then(() => {
        this.isOfflineModalShown = false;
      });
    }
  }

  private hideInfinityScroll() {
    this.isInfinityScrollShown = false;

    setTimeout(() => {
      this.isInfinityScrollShown = true;
    }, 100);
  }

  private setScrollPosition() {
    this.deliveryContainer.getScrollElement().then((scrollPosition: any) => {
      this.store.dispatch(setScrollPosition({ scrollPosition: scrollPosition.scrollTop }));
    });
  }
}
